import * as React from 'react';

import { classNames } from '@core/utils/functions/class-names';
import Link from 'next/link';

type FeaturedPodcastProps = {
    title: string;
    podcastTitle: string;
    podcastUrl: string;
    podcastShowTitle: string;
    podcastShowDescription?: string;
    podcastShowUrl: string;
    spotifyUrl: string;
    itunesUrl: string;
    rssUrl: string;
    youtubeUrl: string;
    className?: string;
};

const FeaturedPodcast = (props: FeaturedPodcastProps) => {
    const {
        title,
        podcastTitle,
        podcastUrl,
        podcastShowTitle,
        podcastShowDescription,
        podcastShowUrl,
        spotifyUrl,
        itunesUrl,
        rssUrl,
        youtubeUrl,
        className,
    } = props;

    return (
        <div className={classNames('flex flex-col justify-center items-start w-full gap-3 p-10', className)}>
            <div className="flex flex-col justify-start items-start gap-2">
                <div className="flex justify-start items-center relative gap-1">
                    <svg
                        width={12}
                        height={13}
                        viewBox="0 0 12 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3 h-3 relative"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <path
                            d="M7 6.75C7 7.12 6.8 7.44 6.5 7.61V11.75H5.5V7.61C5.2 7.435 5 7.12 5 6.75C5 6.2 5.45 5.75 6 5.75C6.55 5.75 7 6.2 7 6.75ZM6 3.75C4.345 3.75 3 5.095 3 6.75C3 7.62 3.375 8.405 3.97 8.95L4.68 8.24C4.265 7.875 4 7.345 4 6.75C4 5.645 4.895 4.75 6 4.75C7.105 4.75 8 5.645 8 6.75C8 7.345 7.735 7.875 7.32 8.24L8.03 8.95C8.625 8.405 9 7.62 9 6.75C9 5.095 7.655 3.75 6 3.75ZM6 1.75C3.24 1.75 1 3.99 1 6.75C1 8.175 1.6 9.455 2.555 10.37L3.265 9.66C2.49 8.93 2 7.895 2 6.75C2 4.545 3.795 2.75 6 2.75C8.205 2.75 10 4.545 10 6.75C10 7.895 9.51 8.93 8.735 9.66L9.445 10.37C10.4 9.455 11 8.175 11 6.75C11 3.99 8.76 1.75 6 1.75Z"
                            fill="#5637CD"
                        />
                    </svg>
                    <div className="flex justify-start items-center relative gap-1">
                        <p className="text-xs uppercase text-primary">{title}</p>
                    </div>
                    <svg
                        width={5}
                        height={9}
                        viewBox="0 0 5 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-grow-0 flex-shrink-0"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <path d="M4.94 1.69L4 0.75L0 4.75L4 8.75L4.94 7.81L1.88667 4.75L4.94 1.69Z" fill="#5637CD" />
                    </svg>
                    <div className="flex justify-start items-center relative gap-1">
                        <Link href={podcastUrl} prefetch={false} passHref>
                            <a className="text-xs uppercase text-primary hover:text-black">{podcastTitle}</a>
                        </Link>
                    </div>
                </div>
                <div className="flex justify-start items-start relative gap-2.5">
                    <a href={podcastShowUrl} className="flex-grow text-[22px] font-headline hover:text-primary">
                        {podcastShowTitle}
                    </a>
                </div>
            </div>
            <div className="flex justify-start items-start self-stretch relative gap-2.5">
                <p className="flex-grow text-sm text-light-gray">{podcastShowDescription}</p>
            </div>
            <div className="flex justify-start items-center gap-2">
                <div className="flex justify-start items-center gap-0.5">
                    <div className="flex justify-start items-center relative gap-1">
                        <p className="text-xxs uppercase text-light-gray">listen</p>
                    </div>
                </div>
                <div className="flex justify-start items-end relative gap-2">
                    <a href={spotifyUrl} target="_blank" rel="noreferrer">
                        <svg
                            width={12}
                            height={13}
                            viewBox="0 0 12 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 relative"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <path
                                d="M6 0.75C2.7 0.75 0 3.45 0 6.75C0 10.05 2.7 12.75 6 12.75C9.3 12.75 12 10.05 12 6.75C12 3.45 9.3 0.75 6 0.75ZM8.775 9.45C8.625 9.6 8.475 9.675 8.25 9.6C6.825 8.7 5.1 8.55 3 9C2.775 9.075 2.625 8.925 2.55 8.7C2.475 8.475 2.625 8.325 2.85 8.25C5.1 7.725 7.125 7.95 8.7 8.925C8.85 9 8.85 9.225 8.775 9.45ZM9.45 7.8C9.3 8.025 9 8.1 8.85 7.95C7.2 6.975 4.8 6.675 2.85 7.275C2.625 7.275 2.4 7.2 2.325 6.9C2.25 6.675 2.4 6.45 2.625 6.375C4.8 5.7 7.5 6.075 9.375 7.2C9.525 7.275 9.6 7.575 9.45 7.8ZM9.525 6.075C7.575 4.95 4.425 4.8 2.55 5.4C2.25 5.475 1.95 5.325 1.875 5.025C1.8 4.725 1.95 4.425 2.25 4.35C4.35 3.75 7.875 3.825 10.125 5.175C10.425 5.325 10.5 5.7 10.35 5.925C10.2 6.15 9.825 6.225 9.525 6.075Z"
                                fill="#6A6A6A"
                            />
                        </svg>
                    </a>
                    <a href={itunesUrl} target="_blank" rel="noreferrer">
                        <svg
                            width={11}
                            height={13}
                            viewBox="0 0 11 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 relative"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <path
                                d="M5.02862 12.6216C4.56776 12.4568 4.46907 12.2329 4.27983 10.92C4.06012 9.39437 4.01213 8.45002 4.13949 8.15093C4.3082 7.75435 4.76665 7.5292 5.40708 7.52679C6.04299 7.52407 6.50536 7.75133 6.67468 8.15093C6.80264 8.44941 6.75465 9.39437 6.53464 10.92C6.38524 11.986 6.30315 12.2555 6.09702 12.4366C5.81392 12.6874 5.41221 12.7568 5.03103 12.6219L5.02862 12.6216ZM3.06023 11.0561C1.4688 10.2732 0.44929 8.9489 0.0786701 7.18695C-0.0139848 6.73213 -0.0296789 5.64773 0.0578453 5.23003C0.289634 4.11002 0.732687 3.23418 1.4688 2.45974C2.52965 1.34124 3.89442 0.75 5.41342 0.75C6.91703 0.75 8.27637 1.33038 9.31157 2.41537C10.0993 3.23418 10.542 4.10067 10.7687 5.24241C10.846 5.62238 10.846 6.65849 10.7738 7.08585C10.5334 8.44488 9.78852 9.66297 8.68834 10.4963C8.29599 10.7917 7.33926 11.3078 7.18473 11.3078C7.12799 11.3078 7.12286 11.2493 7.14882 11.0118C7.19499 10.6306 7.24147 10.5515 7.45757 10.4607C7.80283 10.3167 8.38985 9.89839 8.75021 9.53683C9.37859 8.90879 9.82518 8.12232 10.0425 7.2609C10.1765 6.73062 10.1612 5.55146 10.0124 5.00549C9.54245 3.26858 8.12697 1.9195 6.39188 1.55915C5.88696 1.45593 4.97037 1.45593 4.46031 1.55915C2.7044 1.9195 1.2524 3.33559 0.808441 5.11716C0.690132 5.60005 0.690132 6.78042 0.808441 7.26452C1.1021 8.44368 1.86477 9.52505 2.86315 10.1688C3.03283 10.2822 3.21011 10.3837 3.39373 10.4727C3.60982 10.5654 3.6563 10.6426 3.69734 11.0235C3.723 11.2553 3.71787 11.3223 3.66143 11.3223C3.62521 11.3223 3.36264 11.2091 3.08467 11.0751L3.06023 11.0561ZM3.08075 8.96188C2.54504 8.53452 2.07151 7.77638 1.87563 7.03303C1.75732 6.58394 1.75732 5.73013 1.88076 5.28255C2.20521 4.0735 3.09614 3.13609 4.33205 2.69575C4.75458 2.54666 5.69018 2.51467 6.2114 2.62785C8.00323 3.02623 9.27535 4.82078 9.02938 6.60084C8.93159 7.31824 8.68441 7.90737 8.24679 8.45454C8.0304 8.731 7.50525 9.19518 7.4126 9.19518C7.3972 9.19518 7.38241 9.02013 7.38241 8.80675V8.41863L7.65042 8.09932C8.65966 6.89028 8.58753 5.20196 7.48563 4.09312C7.05827 3.66184 6.56391 3.40832 5.92529 3.29152C5.51332 3.21516 5.4258 3.21516 4.99331 3.28639C4.33657 3.39353 3.82803 3.64856 3.37622 4.09463C2.26708 5.18929 2.19495 6.88816 3.20449 8.09811L3.47008 8.41863V8.81098C3.47008 9.02737 3.45288 9.20333 3.43175 9.20333C3.41063 9.20333 3.26183 9.10041 3.10218 8.97154L3.08075 8.96188ZM4.86745 6.85949C4.40931 6.64611 4.16213 6.24411 4.157 5.71987C4.157 5.24874 4.41957 4.83768 4.87258 4.59714C5.16111 4.44624 5.67086 4.44624 5.95909 4.59714C6.2791 4.76995 6.51921 5.06061 6.6285 5.40749C6.92729 6.42308 5.85104 7.31281 4.87801 6.85828L4.86745 6.85949Z"
                                fill="#6A6A6A"
                            />
                            <path
                                d="M5.49391 7.05984C6.20315 7.05984 6.7781 6.48489 6.7781 5.77565C6.7781 5.06641 6.20315 4.49146 5.49391 4.49146C4.78467 4.49146 4.20972 5.06641 4.20972 5.77565C4.20972 6.48489 4.78467 7.05984 5.49391 7.05984Z"
                                fill="#6A6A6A"
                            />
                            <path
                                d="M5.61562 7.55688C5.76124 7.57351 5.90515 7.6028 6.04569 7.64441C6.19944 7.69339 6.34464 7.76598 6.47607 7.8596C6.58955 7.94199 6.67194 8.0274 6.72325 8.13122C6.77456 8.23505 6.80051 8.35879 6.8159 8.5613C6.82617 8.76412 6.82617 9.04419 6.77999 9.57055C6.73381 10.0969 6.64086 10.862 6.56872 11.3536C6.49659 11.8453 6.44528 12.0644 6.37828 12.224C6.33349 12.3491 6.25745 12.4607 6.1574 12.5481C6.05736 12.6355 5.93662 12.6959 5.80666 12.7235C5.70857 12.7441 5.60834 12.7528 5.50817 12.7492C5.40791 12.7524 5.30763 12.7438 5.20938 12.7235C5.09107 12.6976 4.93776 12.6514 4.82307 12.5587C4.70476 12.4661 4.6275 12.3321 4.56563 12.157C4.50376 11.982 4.45758 11.7707 4.39571 11.3485C4.33384 10.9263 4.25145 10.2982 4.20014 9.81923C4.14883 9.34027 4.12801 9.01069 4.12378 8.78916C4.11956 8.56764 4.12891 8.45718 4.15396 8.35154C4.17796 8.25059 4.21976 8.15473 4.2774 8.06845C4.33456 7.98761 4.40413 7.91631 4.48354 7.85718C4.55638 7.80101 4.63794 7.75717 4.72498 7.72741C4.83113 7.68643 4.94085 7.65541 5.05274 7.63475C5.18645 7.6091 5.34097 7.58344 5.42035 7.57288C5.47566 7.56109 5.53284 7.56109 5.58815 7.57288L5.61562 7.55688Z"
                                fill="#6A6A6A"
                            />
                        </svg>
                    </a>
                    <a href={rssUrl} target="_blank" rel="noreferrer">
                        <svg
                            width={13}
                            height={13}
                            viewBox="0 0 13 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 relative"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <path
                                d="M2.50863 12.75C3.43715 12.75 4.18986 11.9973 4.18986 11.0688C4.18986 10.1402 3.43715 9.38753 2.50863 9.38753C1.58011 9.38753 0.827393 10.1402 0.827393 11.0688C0.827393 11.9973 1.58011 12.75 2.50863 12.75Z"
                                fill="#6A6A6A"
                            />
                            <path
                                d="M0.827393 0.75V2.93252C6.24899 2.93252 10.6449 7.32841 10.6449 12.75H12.8274C12.8274 6.12532 7.45207 0.75 0.827393 0.75ZM0.827393 5.11504V7.29756C3.8351 7.29756 6.27983 9.74229 6.27983 12.75H8.46235C8.46235 8.53149 5.0459 5.11504 0.827393 5.11504Z"
                                fill="#6A6A6A"
                            />
                        </svg>
                    </a>
                    <a href={youtubeUrl} target="_blank" rel="noreferrer">
                        <svg
                            width={16}
                            height={11}
                            viewBox="0 0 16 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-6 h-5 relative"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M14.8919 2.90657C14.8919 2.90657 14.7532 1.92715 14.3253 1.49699C13.7853 0.931545 13.1794 0.928076 12.9019 0.894542C10.9153 0.75 7.93077 0.75 7.93077 0.75C7.93077 0.75 4.94742 0.75 2.95852 0.894542C2.681 0.928076 2.07624 0.930389 1.53507 1.49699C1.10491 1.92715 0.968466 2.90657 0.968466 2.90657C0.880589 3.67055 0.833498 4.43868 0.827393 5.20768L0.827393 6.28423C0.833607 7.05322 0.880698 7.82134 0.968466 8.58534C0.968466 8.58534 1.10723 9.56475 1.5316 10.0007C2.07277 10.5673 2.78276 10.5488 3.10075 10.6089C4.23743 10.7176 7.93192 10.75 7.93192 10.75C7.93192 10.75 10.9187 10.75 12.9042 10.6031C13.1829 10.5696 13.7876 10.5661 14.3288 10.0007C14.7555 9.57054 14.8942 8.58996 14.8942 8.58996C14.9821 7.82598 15.0292 7.05785 15.0353 6.28885V5.20652C15.0276 4.43748 14.9793 3.66938 14.8908 2.90541L14.8919 2.90657Z"
                                fill="#6A6A6A"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6.46118 7.58731V3.59448L10.2991 5.59726L6.46118 7.58731Z"
                                fill="white"
                            />
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default FeaturedPodcast;
